import { Component, computed, inject, Input } from "@angular/core";
import { SymbolFormDirective } from "@bitwarden/web-vault/app/gloss/manage/manage-symbols/symbol-form/symbol-form.directive";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { HelperNumericInput } from "@bitwarden/web-vault/app/shared/utils/helper-numeric-input";
import { isCurrency } from "@bitwarden/web-vault/app/models/view/symbol/currency-utility";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import {
  IReferenceData,
  ReferenceDataSource,
  ReferenceDataView,
  toReferenceDataView,
} from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { TestLog } from "@bitwarden/web-vault/app/services/syncing/test.log";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { GlobalService } from "@bitwarden/common/services/global/global.service";
import { getSourceFromConfig } from "@bitwarden/web-vault/app/services/store/data-plugin/external.data-bank.store.collection";
import {
  fromReferenceData,
  ISymbolView,
} from "@bitwarden/web-vault/app/models/view/symbol/symbol.view";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import {
  SymbolGroupEnum,
  symbolGroupDropdownOptions,
} from "@bitwarden/web-vault/app/models/types/symbol-group.types";
import { DropdownSelectMapOption } from "@bitwarden/web-vault/app/directives/dropdown.directive";

@Component({
  selector: "app-add-symbol-form",
  templateUrl: "./add-symbol-form.component.html",
})
export class AddSymbolFormComponent extends SymbolFormDirective {
  @Input() closeDialog: CallableFunction;
  @Input() onSymbolCreated?: (symbol: ISymbolView) => void;
  /** True while the symbol is being saved; the panel won't close on click-away/Escape. */
  protected submitting = false;

  private transactionStore = inject(TransactionStoreService);
  private globalService = inject(GlobalService);
  private testLog = new TestLog(inject(LogService));
  private confirmationDialogService: ConfirmationDialogService = inject(ConfirmationDialogService);
  private i18nService = inject(I18nService);
  formBuilder: FormBuilder = inject(FormBuilder);
  formGroup: FormGroup;

  protected groupOptions: DropdownSelectMapOption[] = symbolGroupDropdownOptions(this.i18nService);

  constructor() {
    super();
    this.formGroup = this.formBuilder.group({
      name: [null, [Validators.required, Validators.pattern(/\S/)]],
      currency: [this.defaultSystemCurrency, Validators.required],
      price: [null, [HelperNumericInput.isNumberValidator, Validators.required]],
      date: [this.datePipe.transform(Date.now(), "YYYY-MM-dd"), Validators.required],
      group: [SymbolGroupEnum.other, Validators.required],
    });
  }

  // Glide effect for the Find Price / Manually Enter Price toggle.
  protected activeTabIndex = computed(() =>
    this.menu().options.findIndex((o) => o.value === this.currentTab()),
  );

  // Restrict the date field to today or earlier.
  protected dateFilter = (d: Date | null): boolean => !!d && d <= new Date();

  onDatePicked(date: Date) {
    this.onDateChange(this.datePipe.transform(date, "yyyy-MM-dd")!);
  }

  // todo start fix these duplicates
  onInput(value: string, controlName: string) {
    this.formGroup.get(controlName)?.setValue(value);
  }

  onDateChange(date: string) {
    this.formGroup.controls.date.setValue(date);
    this.formGroup.controls.date.markAsTouched();
    if (this.enableFindPrice()) {
      this.onSearchInputChange(this.symbolInput(), date);
    }
  }
  // todo end fix these duplicate methods

  async submit() {
    if (this.enableFindPrice() && this.searchedSymbol()) {
      this.formGroup.controls.currency.setValue(this.searchedSymbol().symbol);
      this.formGroup.controls.price.setValue(this.searchedSymbol().rate);
    }

    this.formGroup.markAllAsTouched();

    if (!this.formGroup.valid) {
      return;
    }

    this.submitting = true;
    try {
      await this.submitAdd();
    } finally {
      this.submitting = false;
    }
  }

  async submitAdd() {
    try {
      if (!this.isValidName()) {
        return;
      }

      const createdSymbol = await this.addSymbol();
      this.onSymbolCreated?.(createdSymbol);
      if (typeof this.closeDialog === "function") {
        this.closeDialog();
      }
    } catch (error) {
      this.testLog.testLog({ error });
    }
  }

  private isValidName() {
    try {
      if (this.isExistingName()) {
        return false;
      }
      return !this.isCurrencyName();
    } catch (error) {
      this.testLog.testLog({ error });
      return false;
    }
  }

  private isExistingName(): boolean {
    try {
      const enteredName = this.formGroup.get("name").value;
      const symbols = this.transactionStore.generatedSymbols.collection();
      if (symbols.length === 0) {
        return false;
      }
      const isExist = symbols.some(
        (symbol) =>
          enteredName.toLowerCase().replace(/\s+/g, "") ===
          symbol.code.toLowerCase().replace(/\s+/g, ""),
      );
      if (isExist) {
        this.confirmationDialogService.notify("error", "error", "symbolNameExists");
      }
      return isExist;
    } catch (error) {
      this.confirmationDialogService.notify("error", "error", "somethingWentWrong");
      throw error;
    }
  }

  private isCurrencyName(): boolean {
    try {
      const enteredName = this.formGroup.get("name").value;
      const isCurrencyName = isCurrency(enteredName);
      if (isCurrencyName) {
        this.confirmationDialogService.notify("error", "error", "symbolNameIsCurrency");
      }
      return isCurrencyName;
    } catch (error) {
      this.confirmationDialogService.notify("error", "error", "somethingWentWrong");
      throw error;
    }
  }

  private async addSymbol() {
    const referenceData = this.formGroupToView();
    await this.transactionStore.symbols.save(referenceData);
    await this.transactionStore.referenceData.save(referenceData.c_asOf as ReferenceDataView);
    this.globalService.showSuccessMessage("success", "newSymbolAdded");
    return referenceData;
  }

  private formGroupToView() {
    const source = this.enableFindPrice()
      ? getSourceFromConfig(this.dataPluginStoreService.databank.config())
      : ReferenceDataSource.MANUAL;
    const name = this.formGroup.get("name").value;
    // #4566 (unresolved product call): with Find Price on, `code` becomes the searched ticker
    // while `name` stays what the user typed, so the symbols list and transaction dropdown
    // label the symbol by ticker while the edit form labels it by name. Decide which wins
    // before changing either.
    const base = this.enableFindPrice() ? this.symbolInput() : name;
    const rd: IReferenceData = {
      // TODO: fix the dateformat conversion
      date: {
        date: this.formGroup.get("date").value,
        time: null,
        timeZone: null,
        reliableTimeSource: true,
      },
      rate: this.formGroup.get("price").value,
      symbol: this.formGroup.get("currency").value,
      base: base,
      source,
    };
    const referenceDataView = toReferenceDataView(rd);
    const view = fromReferenceData(referenceDataView, name);
    view.group = this.formGroup.get("group").value;
    return view;
  }
}
